import { DEFAULT_LANGUAGE } from '@cibli/shared';
import insigneAnime from '@cibli/shared/assets/insigne-vert-noir-anime.svg';
import { Briefcase, FileText, LogOut, Mic, Plus, Search, User as UserIcon } from 'lucide-react';
import { ComponentType, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { useLanguageContext } from '@/contexts/language.context';
import { useUserContext } from '@/contexts/user.context';
import { useResetToHome } from '@/features/common/hooks/useResetToHome';
import { useCVList } from '@/features/cv/hooks/useCVList';
import { useActivityFeed } from '@/hooks/useActivityFeed';
import { useUserJobApplications } from '@/hooks/useUserJobApplications';
import { getInterviewUrl } from '@/lib/interview-url';

import { ReleaseNameBadge } from './ReleaseNameBadge';

type RouteId = 'cvs' | 'candidatures' | 'jobs' | 'profile';

interface NavItem {
  id: RouteId;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
  path: string;
}

const DESKTOP_NAV: NavItem[] = [
  { id: 'cvs', labelKey: 'dashboard.layout.navigation.cvs', icon: FileText, path: '/' },
  {
    id: 'candidatures',
    labelKey: 'dashboard.layout.navigation.applications',
    icon: Briefcase,
    path: '/applications',
  },
  { id: 'jobs', labelKey: 'dashboard.layout.navigation.apply', icon: Search, path: '/jobs' },
  {
    id: 'profile',
    labelKey: 'dashboard.layout.navigation.profile',
    icon: UserIcon,
    path: '/profile',
  },
];

const MOBILE_NAV: NavItem[] = [
  { id: 'cvs', labelKey: 'dashboard.layout.navigation.cvs', icon: FileText, path: '/' },
  {
    id: 'candidatures',
    labelKey: 'dashboard.layout.navigation.applicationsShort',
    icon: Briefcase,
    path: '/applications',
  },
  { id: 'jobs', labelKey: 'dashboard.layout.navigation.apply', icon: Search, path: '/jobs' },
  {
    id: 'profile',
    labelKey: 'dashboard.layout.navigation.profile',
    icon: UserIcon,
    path: '/profile',
  },
];

function getInitials(firstName?: string, lastName?: string): string {
  const f = firstName?.trim()?.[0] ?? '';
  const l = lastName?.trim()?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

function resolveRouteId(pathname: string): RouteId {
  if (pathname.startsWith('/applications')) return 'candidatures';
  if (pathname.startsWith('/jobs') || pathname.startsWith('/apply')) return 'jobs';
  if (pathname.startsWith('/profile')) return 'profile';
  return 'cvs';
}

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onCreateAction?: () => void;
  createButtonText?: string;
  headerAction?: ReactNode;
  hideTopBar?: boolean;
}

export default function DashboardLayout({
  children,
  title,
  subtitle,
  showSearch = false,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onCreateAction,
  createButtonText,
  headerAction,
  hideTopBar = false,
}: DashboardLayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout } = useUserContext();
  const { setLanguage } = useLanguageContext();
  const { resetToHome } = useResetToHome();
  const syncedFromUserRef = useRef(false);
  const { activities } = useActivityFeed(3);
  const { cvs } = useCVList();
  const { applications, error: applicationsError } = useUserJobApplications();

  const navBadges: Partial<Record<RouteId, { count: number; class: string }>> = {
    cvs: { count: cvs.length, class: 'bg-cj-green/15 text-cj-green-nuance' },
    ...(applicationsError
      ? {}
      : { candidatures: { count: applications.length, class: 'bg-cj-pink/15 text-cj-pink' } }),
  };

  const activeRoute = useMemo(() => resolveRouteId(location.pathname), [location.pathname]);
  const initials = getInitials(user?.firstName, user?.lastName);
  const fullName =
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
    t('dashboard.layout.navigation.profile');

  useEffect(() => {
    if (user?.localeId && !syncedFromUserRef.current) {
      syncedFromUserRef.current = true;
      void setLanguage(user.localeId);
    }
  }, [user?.localeId, setLanguage]);

  const handleLogout = useCallback(async () => {
    await setLanguage(DEFAULT_LANGUAGE);
    await logout();
    resetToHome();
  }, [logout, setLanguage, resetToHome]);

  const handleVoiceStart = useCallback(() => {
    const url = new URL(getInterviewUrl());
    url.searchParams.set('source', 'Dashboard');
    window.location.href = url.toString();
  }, []);

  return (
    <div className="min-h-screen">
      <div className="gradient-orb w-80 h-80 bg-cj-blue/10 bottom-[20%] left-[-20%]" />

      {/* ====== DESKTOP SIDEBAR ====== */}
      <aside
        aria-label={t('dashboard.layout.primaryNav')}
        className="hidden lg:flex fixed left-0 top-0 w-64 h-screen flex-col bg-white border-r border-cj-green-dark/5 z-40"
      >
        <div className="p-5 border-b border-cj-green-dark/5">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo-ciblijob.svg" alt="CibliJob" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="p-4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-cj-green-light/20">
            <div className="w-10 h-10 bg-gradient-to-br from-cj-green to-cj-green-nuance rounded-xl flex items-center justify-center">
              <span className="text-white text-xs font-display">{initials}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-cj-green-dark truncate">{fullName}</p>
              <p className="text-[10px] text-cj-green-dark/50">{t('dashboard.layout.candidate')}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1">
          {DESKTOP_NAV.map(item => {
            const Icon = item.icon;
            const isActive = activeRoute === item.id;
            const badge = navBadges[item.id];
            return (
              <Link
                key={item.id}
                to={item.path}
                aria-current={isActive ? 'page' : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive
                    ? 'sidebar-nav-active'
                    : 'text-cj-green-dark/60 hover:bg-cj-green-light/20'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span>{t(item.labelKey)}</span>
                {badge && badge.count > 0 && (
                  <span
                    className={`ml-auto ${badge.class} text-[10px] font-bold px-2 py-0.5 rounded-md`}
                  >
                    {badge.count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {activities.length > 0 && (
          <div className="px-3 py-2">
            <div className="pro-card p-3">
              <h4 className="font-display text-[10px] text-cj-green-dark mb-2">
                {t('dashboard.layout.recentActivity')}
              </h4>
              <div className="space-y-2">
                {activities.map(activity => (
                  <div key={activity.id} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-cj-green rounded-full mt-[3px] flex-shrink-0" />
                    <p className="text-[10px] text-cj-green-dark/60 leading-tight line-clamp-2">
                      {activity.title}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Voice CTA mini */}
        <div className="px-4 pb-3">
          <div className="bg-gradient-to-br from-cj-green-light to-cj-cream rounded-xl p-4 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-cj-green/15 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="h-24 mb-2 flex items-center justify-center">
                <img src={insigneAnime} alt="Ondes CibliJob" className="h-20" />
              </div>
              <p className="font-display text-[10px] text-cj-green-dark mb-1">
                {t('dashboard.layout.voiceCta')}
              </p>
              <button
                onClick={handleVoiceStart}
                className="pro-btn bg-cj-green text-white rounded-lg px-5 py-2.5 text-xs font-semibold inline-flex items-center gap-1.5 w-full justify-center"
              >
                <Mic className="w-4 h-4" /> {t('dashboard.layout.voiceStart')}
              </button>
            </div>
          </div>
        </div>

        <div className="p-3 border-t border-cj-green-dark/5">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-cj-pink hover:bg-cj-pink/5 transition"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('dashboard.layout.logout')}</span>
          </button>
        </div>
      </aside>

      {/* ====== MAIN CONTENT ====== */}
      <div className="lg:ml-64 relative z-10 h-screen flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden flex-shrink-0 sticky top-0 z-30 bg-cj-cream/90 backdrop-blur-lg border-b border-cj-green-dark/5 px-5 pt-5 pb-3">
          <div className="flex items-center justify-between mb-3">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/logo-ciblijob.svg" alt="CibliJob" className="h-10 w-auto" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-cj-green to-cj-green-nuance rounded-xl flex items-center justify-center">
                <span className="text-white text-[10px] font-display">{initials}</span>
              </div>
            </div>
          </div>
          <div>
            <p className="text-cj-green-dark/50 text-xs font-light">
              {t('dashboard.layout.hello')}
            </p>
            <h2 className="font-display text-lg text-cj-green-dark leading-tight">{fullName}</h2>
          </div>
        </header>

        {/* Desktop top bar */}
        {!hideTopBar && (
          <header className="hidden lg:flex flex-shrink-0 sticky top-0 z-30 bg-cj-cream/90 backdrop-blur-lg border-b border-cj-green-dark/5 px-8 py-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="font-display text-xl text-cj-green-dark">{title}</h2>
                {subtitle && <p className="text-sm text-cj-green-dark/40 mt-0.5">{subtitle}</p>}
              </div>
              {headerAction}
            </div>
            <div className="flex items-center gap-3">
              {showSearch && (
                <div className="relative">
                  <Search className="w-4 h-4 text-cj-green-dark/30 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder ?? t('dashboard.layout.searchPlaceholder')}
                    value={searchValue ?? ''}
                    onChange={event => onSearchChange?.(event.target.value)}
                    className="w-80 h-9 bg-white rounded-md pl-10 pr-4 text-sm border border-input shadow-xs outline-none transition"
                  />
                </div>
              )}
              {onCreateAction && (
                <button
                  onClick={onCreateAction}
                  className="pro-btn bg-cj-green text-white rounded-xl px-4 py-2 text-sm font-semibold shadow-sm flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  {createButtonText ?? t('dashboard.layout.createButton')}
                </button>
              )}
            </div>
          </header>
        )}

        {/* Content */}
        <main className="flex-1 overflow-auto px-5 lg:px-8 py-5 lg:py-6 pb-28 lg:pb-8">
          {children}
        </main>

        {/* Bottom scroll fade — content melts into the page background */}
        <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none z-20 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* ====== MOBILE BOTTOM NAV ====== */}
      <nav
        aria-label={t('dashboard.layout.mobileNav')}
        className="lg:hidden fixed bottom-0 left-0 w-full z-40"
      >
        <div className="bg-cj-green backdrop-blur-xl px-2 py-1.5 pb-3 shadow-[0_-4px_20px_rgba(37,68,65,0.1)]">
          <div className="grid grid-cols-4 gap-1 max-w-md mx-auto">
            {MOBILE_NAV.map(item => {
              const Icon = item.icon;
              const isActive = activeRoute === item.id;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg transition ${
                    isActive ? 'text-white' : 'text-white/40'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[11px] font-bold uppercase tracking-tight leading-tight text-center">
                    {t(item.labelKey)}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      <ReleaseNameBadge />
    </div>
  );
}
